import type { GeoJSONSource, Map as MLMap } from "maplibre-gl";
import { boundaryEdges, edgeFeatures, hexFeatures, hexFillPaint, HEX_SIZE_M, layerVisibleAtZoom, type HexCell, type HexEdge } from "../../lib/hexCoverage";
import { OUTLINE_MIN_ZOOM } from "../../lib/meshmapperCoverage";
import { setMapZoom } from "./mapZoom";

// One coverage layer on the Live Map, drawn as hexes: the part both the MeshMapper and the FreqMapper layers share. The layer
// owns two map sources and two map layers (fill, and the outline along the outer edge of each merged area), redraws the hexes
// in view after every move, and hides itself below zoom 9. It only paints: a hex answers no hover and no tap, so node dots
// (and the rest of the map) behave exactly as they would without it. What differs between layers (the hexes and their
// colours) is passed in.

export interface HexLayerCells {
  cells: readonly HexCell[];
  /** The latitude the grid is scaled at. */
  refLat: number;
  /** The outline edges of the merged areas (hexCoverage.boundaryEdges), worked out once with the cells. */
  edges: readonly HexEdge[];
}

/** The cells with their outline edges. */
export function layerCells(cells: readonly HexCell[], refLat: number): HexLayerCells {
  return { cells, refLat, edges: boundaryEdges(cells) };
}

export interface HexLayerSpec {
  /** Source and layer ids are built from this: `${id}`, `${id}-edges`, `${id}-fill`, `${id}-line`. */
  id: string;
  /** The map layer to draw under (the nodes), when it exists. Evaluated each time layers are added. */
  before: (map: MLMap) => string | undefined;
  fillColour: unknown;
  lineColour: unknown;
  fillOpacity: number;
  /** The cells to draw. Called the first time the map is zoomed in far enough to draw the layer (below zoom 9 nothing is built);
   * the owner may keep what it returns, so a theme change does not work the hexes out again. */
  load: () => HexLayerCells;
}

interface Ids {
  fillSrc: string;
  edgeSrc: string;
  fill: string;
  line: string;
}

function idsOf(id: string): Ids {
  return { fillSrc: id, edgeSrc: `${id}-edges`, fill: `${id}-fill`, line: `${id}-line` };
}

/** Take the layer off the map (switched off, no data, or hidden by the protocol filter). Safe to call when it is not there. */
export function removeHexLayer(map: MLMap, id: string): void {
  const ids = idsOf(id);
  try {
    for (const l of [ids.line, ids.fill]) if (map.getLayer(l)) map.removeLayer(l);
    for (const s of [ids.edgeSrc, ids.fillSrc]) if (map.getSource(s)) map.removeSource(s);
  } catch {
    /* the style is mid-swap; nothing to remove */
  }
}

/** Draw a layer on the map and keep it drawn as the map moves. Returns the function that stops it (and unhooks the events);
 * it does not remove the layers, so a theme swap can mount again over them. */
export function mountHexLayer(map: MLMap, spec: HexLayerSpec): () => void {
  const ids = idsOf(spec.id);
  const empty = { type: "FeatureCollection", features: [] };
  let cells: HexLayerCells | null = null;

  function ensureLayers(): boolean {
    try {
      if (!map.getSource(ids.fillSrc)) map.addSource(ids.fillSrc, { type: "geojson", data: empty as any, tolerance: 0 });
      if (!map.getSource(ids.edgeSrc)) map.addSource(ids.edgeSrc, { type: "geojson", data: empty as any, tolerance: 0 });
      const before = spec.before(map);
      if (!map.getLayer(ids.fill)) {
        map.addLayer({ id: ids.fill, type: "fill", source: ids.fillSrc, paint: hexFillPaint(spec.fillColour, spec.fillOpacity) as any }, before);
      }
      if (!map.getLayer(ids.line)) {
        map.addLayer(
          {
            id: ids.line, type: "line", source: ids.edgeSrc, minzoom: OUTLINE_MIN_ZOOM,
            paint: { "line-color": spec.lineColour as any, "line-opacity": 1, "line-width": ["interpolate", ["linear"], ["zoom"], OUTLINE_MIN_ZOOM, 0.6, 14, 1] as any },
          },
          before,
        );
      }
      map.setPaintProperty(ids.fill, "fill-color", spec.fillColour as any);
      map.setPaintProperty(ids.fill, "fill-opacity", spec.fillOpacity);
      map.setPaintProperty(ids.fill, "fill-antialias", false);
      map.setPaintProperty(ids.line, "line-color", spec.lineColour as any);
      return true;
    } catch {
      return false; // the style is mid-swap; the next move or theme change draws it
    }
  }

  function redraw() {
    if (!ensureLayers()) return;
    const zoom = map.getZoom();
    setMapZoom(zoom);
    const setBoth = (fills: unknown[], lines: unknown[]) => {
      (map.getSource(ids.fillSrc) as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: fills } as any);
      (map.getSource(ids.edgeSrc) as GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: lines } as any);
    };
    // Below zoom 9 nothing is drawn and no hex geometry is built (not even the outline edges, until the first zoom-in).
    if (!layerVisibleAtZoom(zoom)) {
      setBoth([], []);
      return;
    }
    if (!cells) cells = spec.load();
    const b = map.getBounds();
    const bounds: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    // No outline below zoom 11: the merged areas stand on their fill alone, so its edges are not even built.
    const outline = zoom >= OUTLINE_MIN_ZOOM ? edgeFeatures(cells.edges, HEX_SIZE_M, cells.refLat, bounds) : [];
    setBoth(hexFeatures(cells.cells, HEX_SIZE_M, cells.refLat, bounds), outline);
  }

  redraw();
  map.on("moveend", redraw);
  return () => {
    map.off("moveend", redraw);
  };
}
